import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SplitLines } from "../components/motion/SplitLines";
import { EASE } from "../components/motion/Reveal";
import { SefariaPassage } from "../components/study/SefariaPassage";
import { SOURCE_SHEETS } from "../data/study";

export default function SourceSheet() {
  const { id } = useParams();
  const idx = SOURCE_SHEETS.findIndex((s) => s.id === id);
  if (idx === -1) return <Navigate to="/study" replace />;
  const sheet = SOURCE_SHEETS[idx];
  const next = SOURCE_SHEETS[(idx + 1) % SOURCE_SHEETS.length];

  return (
    <article data-testid={`source-sheet-${sheet.id}`} className="mx-auto max-w-7xl px-5 pt-32 pb-28 sm:px-8 lg:pt-40">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <Link to="/study" data-testid="source-sheet-back" className="link-underline inline-flex items-center gap-2 text-xs font-medium text-ink-soft">
          <ArrowLeft size={14} /> The Study Hall
        </Link>
      </motion.div>

      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <p className="eyebrow">Source sheet {sheet.number}</p>
          <SplitLines
            lines={[sheet.title]}
            delay={0.15}
            className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          />
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className="mt-4 font-serif text-2xl italic text-ink-soft"
          >
            {sheet.subtitle}
          </motion.p>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
          className="self-end text-base leading-relaxed text-ink-soft lg:col-span-4"
        >
          {sheet.intro}
        </motion.p>
      </div>

      <div className="mt-16">
        {sheet.passages.map((p, i) => (
          <SefariaPassage key={p.ref} passage={p} index={i} />
        ))}
        <div className="border-t hairline" />
      </div>

      <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
        Texts courtesy of Sefaria.org · Hebrew and English editions as credited above
      </p>

      <Link
        to={`/study/sources/${next.id}`}
        data-testid="source-sheet-next"
        className="group mt-16 flex items-center justify-between border hairline bg-white p-8 transition-colors duration-500 hover:bg-parchment-deep"
      >
        <span>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">Next sheet</span>
          <span className="mt-2 block font-serif text-3xl leading-tight text-ink">
            <span className="mr-3 text-terracotta">{next.number}</span>
            {next.title}
          </span>
        </span>
        <ArrowRight size={20} className="text-ink transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </article>
  );
}
